import React from 'react';
import dayjs from 'dayjs';

// Default range: last 7 days
export function defaultRange() {
    return {
        from: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        to: dayjs().format('YYYY-MM-DD')
    };
}

// Date range filter with preset buttons
export function DateRangeFilter({ from, to, onChange }) {
    var presets = [
        { label: 'วันนี้', days: 0 },
        { label: '7 วัน', days: 7 },
        { label: '30 วัน', days: 30 },
        { label: '90 วัน', days: 90 }
    ];
    var setPreset = (d) => {
        if (d === 0) {
            var today = dayjs().format('YYYY-MM-DD');
            onChange(today, today);
        } else {
            onChange(dayjs().subtract(d, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD'));
        }
    };
    return (
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
            <span style={{ fontSize: 12, color: '#888' }}><i className="fas fa-calendar-alt"></i> ช่วงเวลา:</span>
            <input type="date" className="form-control form-control-sm" style={{ width: 150 }}
                value={from || ''} onChange={e => onChange(e.target.value, to)} />
            <span style={{ color: '#888' }}>→</span>
            <input type="date" className="form-control form-control-sm" style={{ width: 150 }}
                value={to || ''} onChange={e => onChange(from, e.target.value)} />
            <div className="btn-group btn-group-sm" role="group">
                {presets.map(p => (
                    <button key={p.label} type="button" className="btn btn-outline-secondary"
                        onClick={() => setPreset(p.days)}>{p.label}</button>
                ))}
                <button type="button" className="btn btn-outline-secondary" onClick={() => onChange('', '')}>ทั้งหมด</button>
            </div>
        </div>
    );
}

// Filter records by date range on a given field (defaults to 'timestamp')
export function filterByDate(list, from, to, field = 'timestamp') {
    if (!from && !to) return list;
    return list.filter(item => {
        var t = item[field];
        if (!t) return false;
        var d = dayjs(t).format('YYYY-MM-DD');
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
    });
}

// Sort helper + indicator
export function sortRecords(list, field, dir) {
    if (!field) return list;
    var sorted = [...list];
    sorted.sort((a, b) => {
        var av = a[field], bv = b[field];
        if (av == null) av = '';
        if (bv == null) bv = '';
        if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
        av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
        if (av < bv) return dir === 'asc' ? -1 : 1;
        if (av > bv) return dir === 'asc' ? 1 : -1;
        return 0;
    });
    return sorted;
}

export function SortableHeader({ label, field, sortField, sortDir, onSort, style, width }) {
    var active = sortField === field;
    var nextDir = active && sortDir === 'asc' ? 'desc' : 'asc';
    return (
        <th style={{ cursor: 'pointer', userSelect: 'none', ...(style || {}), ...(width ? { width } : {}) }}
            onClick={() => onSort(field, nextDir)}>
            {label}
            <span style={{ marginLeft: 4, color: active ? '#dc3545' : '#ccc', fontSize: 11 }}>
                {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
            </span>
        </th>
    );
}

// Pagination UI + helper
export function paginate(list, page, pageSize) {
    var start = (page - 1) * pageSize;
    return list.slice(start, start + pageSize);
}

export function Pagination({ total, page, pageSize, onPageChange, onPageSizeChange }) {
    var totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (page > totalPages) page = totalPages;
    var start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    var end = Math.min(page * pageSize, total);

    var pages = [];
    var addPage = (p) => pages.push(p);
    if (totalPages <= 7) {
        for (var i = 1; i <= totalPages; i++) addPage(i);
    } else {
        addPage(1);
        if (page > 3) addPage('...');
        for (var p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) addPage(p);
        if (page < totalPages - 2) addPage('...');
        addPage(totalPages);
    }

    return (
        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 10, padding: '10px 14px', background: '#f8f9fa', borderTop: '1px solid #eee', fontSize: 13 }}>
            <div style={{ color: '#666' }}>
                แสดง {start}-{end} จาก {total} รายการ
            </div>
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                    <span style={{ color: '#666', fontSize: 12 }}>ต่อหน้า:</span>
                    <select className="form-select form-select-sm" style={{ width: 80 }} value={pageSize}
                        onChange={e => onPageSizeChange(parseInt(e.target.value))}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
                <div className="btn-group btn-group-sm" role="group">
                    <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => onPageChange(1)} title="หน้าแรก">
                        <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => onPageChange(page - 1)} title="ก่อนหน้า">
                        <i className="fas fa-angle-left"></i>
                    </button>
                    {pages.map((p, i) => p === '...' ? (
                        <button key={i} className="btn btn-outline-secondary" disabled>...</button>
                    ) : (
                        <button key={i} className={'btn ' + (p === page ? 'btn-danger' : 'btn-outline-secondary')}
                            onClick={() => onPageChange(p)}>{p}</button>
                    ))}
                    <button className="btn btn-outline-secondary" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} title="ถัดไป">
                        <i className="fas fa-angle-right"></i>
                    </button>
                    <button className="btn btn-outline-secondary" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} title="หน้าสุดท้าย">
                        <i className="fas fa-angle-double-right"></i>
                    </button>
                </div>
            </div>
        </div>
    );
}
